import { memo, useMemo, useState } from 'react';
import Image from 'next/image';
import { Box, IconButton, Rating, Typography } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import CloseIcon from '@mui/icons-material/Close';

import {
  updateCartItemCount,
  updatePantryCartItemCount,
  usePersistentCartItems,
  usePersistentPantryCartItems,
} from 'store/cartItems';
import Modal from '@components/DishModal';
import { GetFilledChilli } from '@components/FilledChilli';
import { GetEmptyChilli } from '@components/EmptyChilli';
import { productType } from '@utils/types';
import PopularItem from '@public/assets/svg/PopularItem.svg';
import NewItem from '@public/assets/svg/NewItem.svg';
import ArrowTooltips from '@components/Tooltip';
import { GetVegan } from '@components/Vegan';
import theme from 'src/style-system/theme';

import { useStyles } from './useStyles';

interface DishDisplayProps {
  product: productType;
  isFinalOrder?: boolean;
  isModal?: boolean;
  isSmallSize?: boolean;
  quantity?: number;
  isNoAddMealBtn?: boolean;
  isSubscriptionBtn?: boolean;
  isPantry?: boolean;
  isMoreInfoBtn?: boolean;
  isShowRating?: boolean;
  mode?: string;
  elevarAddToCartEvent?: any;
  elevarRemoveFromCartEvent?: any;
  elevarViewItemEvent?: any;
}

const DishDisplay = ({
  product,
  isFinalOrder,
  isModal,
  isSmallSize,
  quantity,
  isNoAddMealBtn = false,
  isSubscriptionBtn = false,
  isPantry = false,
  isMoreInfoBtn = false,
  isShowRating = false,
  mode,
  elevarAddToCartEvent,
  elevarRemoveFromCartEvent,
  elevarViewItemEvent,
}: DishDisplayProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const handleOpen = (productTitle: string | undefined) => {
    setOpen(true);
    //@ts-ignore
    window?.dataLayer.push({
      text: productTitle,
      event: 'item_open',
    });
  };
  const handleClose = () => setOpen(false);
  const classes = useStyles();

  const cartItems = usePersistentCartItems();
  const pantryCartItems = usePersistentPantryCartItems();

  const decrement = (productId: number, productTitle: string) => {
    // for GA event
    if (cartItems[productId] === 1) {
      //@ts-ignore
      window?.dataLayer.push({
        text: productTitle,
        event: 'item_remove',
      });
    }

    if (!mode && elevarRemoveFromCartEvent) {
      //for elvart remove from cart event
      elevarRemoveFromCartEvent(product);
    }

    if (isPantry) {
      if (pantryCartItems[productId]) {
        updatePantryCartItemCount(productId, pantryCartItems[productId] - 1);
      }
    } else {
      if (cartItems[productId]) {
        updateCartItemCount(productId, cartItems[productId] - 1);
      }
    }
  };
  const increment = (productId: number, productTitle: string) => {
    // for GA event
    //@ts-ignore
    window?.dataLayer.push({
      text: productTitle,
      event: 'item_add',
    });
    if (!mode && elevarAddToCartEvent) {
      //fire elevar add to cart event
      elevarAddToCartEvent(product);
    }

    if (isPantry) {
      if (pantryCartItems[productId]) {
        updatePantryCartItemCount(productId, pantryCartItems[productId] + 1);
      } else {
        updatePantryCartItemCount(productId, 1);
      }
    } else {
      if (cartItems[productId]) {
        updateCartItemCount(productId, cartItems[productId] + 1);
      } else {
        updateCartItemCount(productId, 1);
      }
    }
  };

  const isDecIconButtonDisabled = useMemo(() => {
    return isPantry
      ? !pantryCartItems[product?.id] || pantryCartItems[product?.id] === 0
      : !cartItems[product?.id] || cartItems[product?.id] === 0;
  }, [pantryCartItems, cartItems]);

  const getMealCount = useMemo(() => {
    return (
      (isPantry ? pantryCartItems[product?.id] : cartItems[product?.id]) || 0
    );
  }, [pantryCartItems, cartItems]);

  return (
    <>
      <Box
        className={classes.mainContainer}
        width={{
          xs: '150px',
          sm: isSmallSize ? '175px' : '200px',
          xl: isSmallSize ? '180px' : '220px',
        }}
      >
        <Box
          position="relative"
          width={{
            xs: '120px',
            sm: isSmallSize ? '175px' : '200px',
            xl: isSmallSize ? '180px' : '220px',
          }}
          height={{
            xs: '120px',
            sm: isSmallSize ? '175px' : '200px',
            xl: isSmallSize ? '180px' : '220px',
          }}
          sx={{ cursor: 'pointer' }}
          onClick={isModal ? () => handleOpen(product?.title) : () => {}}
        >
          {product?.product_badge === 'Best Seller' ? (
            <Box className={classes.itemBadge}>
              <ArrowTooltips title="Bestseller">
                <PopularItem />
              </ArrowTooltips>
            </Box>
          ) : null}
          {product?.product_badge === 'Newly Added' ? (
            <Box className={classes.itemBadge}>
              <ArrowTooltips title="New Dish">
                <NewItem />
              </ArrowTooltips>
            </Box>
          ) : null}

          {product?.image_url ? (
            <Image
              src={product?.image_url}
              alt={product?.title}
              objectFit="contain"
              layout="fill"
            />
          ) : null}
        </Box>
        {isShowRating ? (
          <ArrowTooltips title="Your rating">
            <Rating
              name="simple-controlled"
              value={product?.rating}
              sx={{
                paddingTop: '8px',
                [theme.breakpoints.up('sm')]: {
                  transform: 'scale(1.25)',
                },
              }}
              readOnly={true}
            />
          </ArrowTooltips>
        ) : null}
        <Typography
          variant="body2"
          align="center"
          color="garamMasala.main"
          mt={2}
          sx={{ cursor: 'pointer' }}
          id={product.id.toString()}
          onClick={isModal ? () => handleOpen(product?.title) : () => {}}
        >
          {product?.title}
        </Typography>

        <Typography variant="caption" align="center" color="cinnamon.main">
          {product?.taste}
        </Typography>
        {isFinalOrder ? null : product?.spice_level !== 0 ? (
          <Box
            sx={{
              marginTop: '10px',
              display: 'flex',
              alignItems: 'center',
              columnGap: '4px',
            }}
          >
            {product?.vegan ? (
              <ArrowTooltips title="Vegan">
                <GetVegan />
              </ArrowTooltips>
            ) : null}
            {/* TODO: This should be rotated by the some degree or replace with other svg */}
            <ArrowTooltips title="Spice Level">
              <>
                {product?.spice_level >= 1 ? (
                  <GetFilledChilli />
                ) : (
                  <GetEmptyChilli />
                )}
                {product?.spice_level >= 2 ? (
                  <GetFilledChilli />
                ) : (
                  <GetEmptyChilli />
                )}
                {product?.spice_level >= 3 ? (
                  <GetFilledChilli />
                ) : (
                  <GetEmptyChilli />
                )}
                {product?.spice_level >= 4 ? (
                  <GetFilledChilli />
                ) : (
                  <GetEmptyChilli />
                )}
                {product?.spice_level >= 5 ? (
                  <GetFilledChilli />
                ) : (
                  <GetEmptyChilli />
                )}
              </>
            </ArrowTooltips>
          </Box>
        ) : (
          <Box sx={{ marginTop: '10px' }}>
            {product?.vegan ? (
              <ArrowTooltips title="Vegan">
                <GetVegan />
              </ArrowTooltips>
            ) : null}
          </Box>
        )}
        {isNoAddMealBtn || isFinalOrder ? null : (
          <Box className={classes.btnContainer}>
            <IconButton
              onClick={() => decrement(product?.id, product?.title)}
              size="large"
              disabled={isDecIconButtonDisabled}
            >
              <RemoveCircleIcon
                fontSize="inherit"
                sx={{
                  color: !isDecIconButtonDisabled
                    ? 'cinnamon.main'
                    : 'lightGray.main',
                }}
              />
            </IconButton>

            <Typography variant="body2" color="cinnamon.main">
              {quantity !== null && quantity !== undefined
                ? quantity
                : getMealCount}
            </Typography>

            <IconButton
              onClick={() => increment(product?.id, product?.title)}
              size="large"
            >
              <AddCircleIcon
                sx={{ color: 'cinnamon.main' }}
                fontSize="inherit"
              />
            </IconButton>
          </Box>
        )}
        {isFinalOrder ? (
          <Box
            sx={{
              flexGrow: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isFinalOrder ? (
              <CloseIcon fontSize="small" sx={{ color: 'cinnamon.main' }} />
            ) : null}
            <Typography variant="body2" color="cinnamon.main">
              {quantity !== null && quantity !== undefined
                ? quantity
                : getMealCount}
            </Typography>
          </Box>
        ) : null}
        {isMoreInfoBtn ? (
          <Typography
            variant="caption"
            color="garamMasala.main"
            align="center"
            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={isModal ? () => handleOpen(product?.title) : () => {}}
          >
            More Info
          </Typography>
        ) : null}
      </Box>
      {open ? (
        <Modal
          open={open}
          handleClose={handleClose}
          product={product}
          isSubscriptionBtn={isSubscriptionBtn}
          isNoAddMealBtn={isNoAddMealBtn}
          isPantry={isPantry}
          mode={mode}
          elevarAddToCartEvent={elevarAddToCartEvent}
          elevarRemoveFromCartEvent={elevarRemoveFromCartEvent}
          elevarViewItemEvent={elevarViewItemEvent}
        />
      ) : null}
    </>
  );
};

export default memo(DishDisplay);
